import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import OrganizationSelectModal from '../components/OrganizationSelectModal';
import OrganizationMfaModal from '../components/OrganizationMfaModal';
import { authApiClient, endSession } from '../../../utils/axios';
import {
  applySelectedSession,
  finalizeOrganizationAccess,
  resolveSafePostAuthPath,
  selectOrganization,
} from '../../../utils/authToken';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { consumePostAuthRedirect, readAuthEmail, readPendingOrgMfaContext } from '../../../utils/authSession';
import appConfig from '../../../config/appConfig';

const SelectOrganizationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { login: l } = useTranslatedMessages();
  const orgSelection = l.org_selection ?? {};

  const [redirectTo] = useState(() =>
    resolveSafePostAuthPath(location.state?.redirectTo || consumePostAuthRedirect('/dashboard')),
  );
  const email = location.state?.email || readAuthEmail();

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mfaState, setMfaState] = useState(() => readPendingOrgMfaContext());
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  useEffect(() => {
    const pendingMfa = readPendingOrgMfaContext();

    if (pendingMfa?.organizationId) {
      setMfaState(pendingMfa);
    }
  }, []);

  const completeSelection = (token) => {
    applySelectedSession(dispatch, queryClient);
    navigate(redirectTo, { replace: true });
  };

  const handleMfaCancel = () => {
    setMfaState(null);
  };

  const selectOrganizationById = async (organizationId) => {
    setSubmitting(true);
    setError(null);

    const selectedOrg = organizations.find((organization) => organization.id === organizationId);

    try {
      const selectResponse = await selectOrganization(authApiClient, organizationId);

      await finalizeOrganizationAccess({
        selectResponse,
        organizationId,
        organizationName: selectedOrg?.name,
        email,
        dispatch,
        queryClient,
        persistPendingMfa: true,
        onMfaRequired: (payload) => {
          if (payload.message) {
            dispatch(
              showSnackbar({
                message: payload.message,
                severity: 'success',
              }),
            );
          }

          setMfaState({
            organizationId,
            organizationName: selectedOrg?.name,
            email,
          });
        },
      }).then((result) => {
        if (result.completed) {
          navigate(redirectTo, { replace: true });
        }
      });
    } catch (err) {
      const alreadySent =
        err?.response?.status === 429 ||
        String(err?.response?.data?.errors?.message || '').includes('OTP already sent');

      if (alreadySent) {
        setMfaState({
          organizationId,
          organizationName: selectedOrg?.name,
          email,
        });

        return;
      }

      const message =
        err?.response?.data?.errors?.message ||
        err?.response?.data?.message ||
        orgSelection.select_error ||
        'Unable to select organization. Please try again.';

      setError(message);
      dispatch(
        showSnackbar({
          message,
          severity: 'error',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadOrganizations = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authApiClient.get('/organizations');
        const items = response.data?.organizations ?? [];

        if (cancelled) {
          return;
        }

        setOrganizations(items);

        if (items.length === 1) {
          setLoading(false);
          await selectOrganizationById(items[0].id);

          return;
        }
      } catch (err) {
        if (cancelled) {
          return;
        }

        const message =
          err?.response?.data?.errors?.message ||
          err?.response?.data?.message ||
          orgSelection.load_error ||
          'Unable to load organizations. Please try again.';

        setError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSelection.load_error]);

  const handleLogout = () => {
    queryClient.clear();
    endSession().finally(() => {
      navigate('/login', { replace: true });
    });
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <Grid
        container
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          p: '20px',
          opacity: 0.45,
          pointerEvents: 'none',
          filter: 'blur(1px)',
        }}
      >
        <Grid
          size={{ xs: 0, lg: 7, xl: 6 }}
          sx={{
            display: { sm: 'none', lg: 'flex' },
          }}
        >
          <img
            src={appConfig.brandBackgroundImagePath}
            alt="signup background"
            width="100%"
            style={{ display: 'flex' }}
          />
        </Grid>
        <Grid size={{ xs: 1, sm: 2, md: 3, lg: 1, xl: 2 }} />
        <Grid size={{ xs: 10, sm: 8, md: 6, lg: 3, xl: 3 }}>
          <Stack sx={{ alignItems: 'center', mb: 4 }}>
            <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
          </Stack>
          <Stack spacing={2}>
            <TextField label={l.form?.email_label ?? 'Email'} value="" disabled fullWidth />
            <TextField label={l.form?.password_label ?? 'Password'} type="password" value="" disabled fullWidth />
            <Typography variant="body2" color="text.secondary">
              {l.links?.forgot_password ?? 'Forgot Password?'}
            </Typography>
            <Button variant="contained" disabled fullWidth>
              {l.actions?.sign_in ?? 'Sign In'}
            </Button>
          </Stack>
        </Grid>
        <Grid size={{ xs: 1, sm: 2, md: 3, lg: 1, xl: 1 }} />
      </Grid>

      <OrganizationSelectModal
        showClose={false}
        showSelectedHighlight={false}
        organizations={organizations}
        loading={loading}
        submitting={submitting}
        error={error}
        onSelectOrganization={selectOrganizationById}
        emptyMessage={orgSelection.empty ?? 'No organizations available.'}
      />

      <OrganizationMfaModal
        open={Boolean(mfaState?.organizationId)}
        email={email}
        organizationId={mfaState?.organizationId}
        organizationName={mfaState?.organizationName}
        submitting={mfaSubmitting}
        setSubmitting={setMfaSubmitting}
        onVerified={completeSelection}
        onCancel={handleMfaCancel}
      />

      <Box sx={{ position: 'fixed', left: 24, bottom: 24, zIndex: 1400 }}>
        <Button variant="text" onClick={handleLogout} disabled={submitting || mfaSubmitting}>
          {orgSelection.logout ?? 'Logout'}
        </Button>
      </Box>
    </Box>
  );
};

export default SelectOrganizationPage;
